/**
 * @module agent/adapters/services/SessionManager
 *
 * Component-based session management with encapsulated state.
 * Follows ARCHITECTURE.md design: Components own their domain state.
 *
 * Phase 4 Acceptance Criteria:
 * - AC2: SessionManager class owns `sessions` Map state
 * - AC3: Receives dependencies via constructor (DI pattern)
 * - AC5: Independently testable with mocked dependencies
 *
 * @example
 * import SessionManager from './services/SessionManager';
 * import * as storageUtils from './adapterStorageUtils';
 *
 * const manager = new SessionManager(store, {
 *   loadStoredSessions: storageUtils.loadStoredSessions,
 *   saveStoredSessions: storageUtils.saveStoredSessions,
 *   getCurrentUserId: storageUtils.getCurrentUserId
 * });
 * manager.load();
 * const session = manager.create({ title: 'My Chat' });
 */

const ADAPTER_NAME = 'AGENTCORE';

/**
 * Session manager with encapsulated state and dependency injection.
 */
class SessionManager {
  /**
   * @param {Object} store - MobX store with authStore
   * @param {Object} storageUtils - Storage utility functions (DI)
   * @param {Function} storageUtils.loadStoredSessions - Load sessions from localStorage
   * @param {Function} storageUtils.saveStoredSessions - Save sessions to localStorage
   * @param {Function} [storageUtils.getCurrentUserId] - Get current user ID
   */
  constructor(store, storageUtils) {
    this._store = store;
    this._storageUtils = storageUtils;

    // Encapsulated state - not directly accessible
    this._sessions = new Map();

    // Change callbacks
    this._changeCallbacks = [];
  }

  // ============================================================================
  // PERSISTENCE
  // ============================================================================

  /**
   * Load sessions from storage.
   * Handles date transformation and migration.
   */
  load() {
    const transformer = (session) => {
      // Migrate old sessions: if lastActivity is missing, use createdAt as fallback
      if (session.lastActivity) {
        session.lastActivity = new Date(session.lastActivity);
      } else {
        session.lastActivity = session.createdAt || new Date();
      }
      return session;
    };

    const sessions = this._storageUtils.loadStoredSessions(this._store, ADAPTER_NAME, transformer);

    this._sessions = sessions;
  }

  /**
   * Save sessions to storage.
   */
  save() {
    this._storageUtils.saveStoredSessions(this._sessions, this._store, ADAPTER_NAME);
  }

  // ============================================================================
  // CRUD OPERATIONS
  // ============================================================================

  /**
   * Create a new session.
   *
   * @param {Object} options - Session options
   * @param {string} [options.title='New Conversation'] - Session title
   * @returns {Object} Created session
   */
  create(options = {}) {
    const id = crypto.randomUUID();
    const now = new Date();

    // Spread options first, then override with required fields to ensure
    // id cannot be overwritten by options
    const session = {
      ...options,
      id,
      title: options.title || 'New Conversation',
      createdAt: now,
      lastActivity: now
    };

    this._sessions.set(id, session);
    this.save();
    this._notifyChange();

    return session;
  }

  /**
   * Get session by ID.
   *
   * @param {string} id - Session ID
   * @returns {Object|null} Session or null if not found
   */
  getById(id) {
    return this._sessions.get(id) || null;
  }

  /**
   * Get all sessions sorted by lastActivity (newest first).
   *
   * @returns {Array} Array of sessions (copy, not reference)
   */
  getAll() {
    const sessions = Array.from(this._sessions.values());

    return sessions.sort((a, b) => {
      const aTime = a.lastActivity?.getTime?.() || 0;
      const bTime = b.lastActivity?.getTime?.() || 0;
      return bTime - aTime;
    });
  }

  /**
   * Update session properties.
   *
   * @param {string} id - Session ID
   * @param {Object} updates - Properties to update
   * @throws {Error} If session not found
   */
  update(id, updates) {
    const session = this._sessions.get(id);

    if (!session) {
      throw new Error(`Session not found: ${id}`);
    }

    const updated = {
      ...session,
      ...updates,
      id, // Prevent ID from being changed
      lastActivity: new Date()
    };

    this._sessions.set(id, updated);
    this.save();
    this._notifyChange();
  }

  /**
   * Delete a session.
   *
   * @param {string} id - Session ID
   * @returns {Object} Deleted session data
   * @throws {Error} If session not found
   */
  delete(id) {
    const session = this._sessions.get(id);

    if (!session) {
      throw new Error(`Session not found: ${id}`);
    }

    this._sessions.delete(id);
    this.save();
    this._notifyChange();

    return session;
  }

  /**
   * Update session's lastActivity timestamp.
   * Silent fail for non-existent sessions.
   *
   * @param {string} id - Session ID
   */
  touchActivity(id) {
    const session = this._sessions.get(id);

    if (session) {
      session.lastActivity = new Date();
      this.save();
    }
  }

  /**
   * Migrate session to a new ID.
   * Used when backend returns different ID than client-generated.
   *
   * @param {string} oldId - Current session ID
   * @param {string} newId - New session ID
   */
  updateSessionId(oldId, newId) {
    const session = this._sessions.get(oldId);

    if (session) {
      const migrated = {
        ...session,
        id: newId
      };

      this._sessions.delete(oldId);
      this._sessions.set(newId, migrated);
      this.save();
      this._notifyChange();
    }
  }

  /**
   * Clear all sessions.
   */
  clear() {
    this._sessions.clear();
    this.save();
    this._notifyChange();
  }

  /**
   * Get number of sessions.
   *
   * @returns {number} Session count
   */
  count() {
    return this._sessions.size;
  }

  /**
   * Check if session exists.
   *
   * @param {string} id - Session ID
   * @returns {boolean} True if session exists
   */
  has(id) {
    return this._sessions.has(id);
  }

  /**
   * Set sessions directly (for backend sync).
   * Used by adapter for background refresh operations.
   *
   * @param {Map} sessions - Sessions Map to set
   */
  setAll(sessions) {
    this._sessions = sessions;
    this.save();
    this._notifyChange();
  }

  /**
   * Get the internal sessions Map.
   *
   * @internal For test inspection and validateCache() only. Production code
   * should use manager methods (getById, getAll, set, entries, etc.).
   * Returns direct reference - modifications bypass validation/persistence.
   *
   * @returns {Map} Sessions Map reference
   */
  getMap() {
    return this._sessions;
  }

  // ============================================================================
  // DIRECT MAP OPERATIONS (Phase 4.7 - for adapter migration)
  // ============================================================================

  /**
   * Set session directly without auto-save or callbacks.
   * Used during complex multi-step operations where caller controls persistence.
   *
   * @param {string} id - Session ID
   * @param {Object} session - Session object
   */
  set(id, session) {
    this._sessions.set(id, session);
  }

  /**
   * Delete session by ID without throwing if not found.
   * Returns boolean indicating if deletion occurred.
   * Does NOT auto-save or notify - caller controls persistence.
   *
   * @param {string} id - Session ID
   * @returns {boolean} True if session was deleted, false if not found
   */
  deleteById(id) {
    return this._sessions.delete(id);
  }

  /**
   * Get iterator over [id, session] pairs.
   * Enables for...of iteration over sessions.
   *
   * @returns {Iterator} Iterator over [id, session] entries
   */
  entries() {
    return this._sessions.entries();
  }

  /**
   * Get iterator over session objects.
   * Enables for...of iteration over sessions.
   *
   * @returns {Iterator} Iterator over session values
   */
  values() {
    return this._sessions.values();
  }

  // ============================================================================
  // CALLBACKS
  // ============================================================================

  /**
   * Register callback for session changes.
   *
   * @param {Function} callback - Called when sessions change
   */
  onChange(callback) {
    this._changeCallbacks.push(callback);
  }

  /**
   * Notify all registered callbacks of a change.
   * @private
   */
  _notifyChange() {
    this._changeCallbacks.forEach((cb) => {
      try {
        cb(this.getAll());
      } catch (error) {
        console.error('[SessionManager] Callback error:', error);
      }
    });
  }
}

export default SessionManager;
